import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import { introTrialDaysLeft, introTrialResetOnUtcLabel, introTrialSpanDays, requiresPlanPurchase } from '../../../utils/billingTrial';
import { accountTypeLabel, UserComputeBudgetCard } from './UserComputeBudgetCard';
import { useTheme } from '../../shared/theme/ThemeContext';
import {
  BRAND_GOLD,
  BRAND_GREEN,
  DASH_FONT_SERIF,
  getUserMenuTheme,
  userMenuDropdownClass,
  userMenuTriggerClass,
} from '../../shared/theme/themeStyles';

type Props = {
  /** Opening the profile modal; omitted for guest-only UIs. */
  onOpenProfile?: () => void;
  variant?: 'default' | 'forest';
  /** Skip the avatar trigger and always show the account panel (mobile menu sheet). */
  inline?: boolean;
};

/**
 * Desktop toolbar: profile pill + dropdown with the same account info/actions as the sidebar header.
 */
export default function ToolbarUserMenu({ onOpenProfile, variant = 'default', inline = false }: Props) {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { theme, isDark } = useTheme();
  const um = getUserMenuTheme(isDark);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const introTrialUi = useMemo(() => {
    if (!user || user.is_system_guest) return null;
    const left = introTrialDaysLeft(user);
    if (left == null) return null;
    return {
      left,
      resetOn: introTrialResetOnUtcLabel(user),
      spanDays: introTrialSpanDays(user),
    };
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (authLoading || !user) {
    return null;
  }

  const isGuest = user.is_system_guest;
  const initials = (() => {
    const parts = (user.full_name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    const email = user.email?.trim();
    if (email) return email.slice(0, 2).toUpperCase();
    return 'DS';
  })();

  const menuBody = (
          <div className="space-y-2">
            <div className={um.headerCard}>
              <p className={um.headerLabel}>Signed in as</p>
              <div className="mt-1.5 flex items-center gap-2 min-w-0">
                <span className={um.avatarWrap} style={{ backgroundColor: BRAND_GREEN }}>
                  <i className="ri-user-3-line text-sm" aria-hidden />
                </span>
                <p className={`${um.headerName} truncate`}>{accountTypeLabel(user)}</p>
              </div>
            </div>

            <UserComputeBudgetCard user={user} />

            {!isGuest && introTrialUi && introTrialUi.resetOn && (
              <p className={um.trialText}>
                Intro resets on <span className={um.trialStrong}>{introTrialUi.resetOn}</span>
                {introTrialUi.spanDays != null ? (
                  <>
                    {' '}(<span className="tabular-nums">{introTrialUi.spanDays}</span>-day intro ·{' '}
                    <span className={`tabular-nums font-semibold ${um.trialStrong}`}>{introTrialUi.left}</span> day
                    {introTrialUi.left === 1 ? '' : 's'} left, UTC).
                  </>
                ) : (
                  <>
                    {' '}(
                    <span className={`tabular-nums font-semibold ${um.trialStrong}`}>{introTrialUi.left}</span> day
                    {introTrialUi.left === 1 ? '' : 's'} left, UTC).
                  </>
                )}
              </p>
            )}

            {!isGuest && requiresPlanPurchase(user) && (
              <div className={um.upgradeBanner}>
                <p className={um.upgradeText}>
                  Your free intro period has ended. Choose a plan to keep full analysis access.
                </p>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onOpenProfile?.();
                  }}
                  className={um.upgradeBtn}
                >
                  Pay / upgrade — open profile
                </button>
              </div>
            )}

            {!isGuest && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onOpenProfile?.();
                }}
                className={um.primaryBtn}
              >
                <i className="ri-user-settings-line text-sm" aria-hidden />
                View profile
              </button>
            )}

            {!isGuest && (user.is_superuser || user.role === 'admin') && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  navigate('/admin');
                }}
                className={um.adminBtn}
              >
                <i className="ri-settings-3-line text-sm" aria-hidden />
                Admin panel
              </button>
            )}

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className={um.secondaryBtn}
            >
              Log out
            </button>
          </div>
  );

  if (inline) {
    return menuBody;
  }

  return (
    <div className={`relative shrink-0 ${open ? 'z-[110]' : ''}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          variant === 'forest'
            ? 'flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-semibold text-white'
            : userMenuTriggerClass(theme, open)
        }
        style={variant === 'forest' ? { backgroundColor: BRAND_GOLD, fontFamily: DASH_FONT_SERIF } : undefined}
        title="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {variant === 'forest' ? (
          initials
        ) : (
          <>
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              <i className="ri-user-3-line text-base" />
            </span>
            <i
              className={`ri-arrow-down-s-line text-sm pr-0.5 transition-transform ${isDark ? 'text-gray-400' : 'text-gray-500'} ${open ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </>
        )}
      </button>

      {open && (
        <div role="menu" className={userMenuDropdownClass(theme)}>
          {menuBody}
        </div>
      )}
    </div>
  );
}
